import { CHANNEL_IMAGE, uploadImage } from './FileUploadActionCreators';
import { formatMessage } from 'utils/Message';
import { handleReponse } from 'actions';
import { s3KeyToUrl } from 'utils/images';
import fetchAPI from 'utils/fetchAPI';

export const CLEAR_CURRENT_CHANNEL = 'CLEAR_CURRENT_CHANNEL';
export const clearCurrentChannel = () => ({
  type: CLEAR_CURRENT_CHANNEL,
});

export const SET_CURRENT_CHANNEL = 'SET_CURRENT_CHANNEL';
export const setCurrentChannel = channel => ({
  type: SET_CURRENT_CHANNEL,
  channel,
});

export function getChannel({ companyId, channelId }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      dispatch(clearCurrentChannel());
      const token = getState().users.token;
      Promise.all([
        fetchAPI({
          endpoint: `/companies/${companyId}/channel/${channelId}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).then(res => {
          if (res.status === 200) return res.json();
          throw new Error(res.statusText);
        }),
        fetchAPI({
          endpoint: `/companies/${companyId}/channel/${channelId}/followers`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).then(res => {
          if (res.status === 200) return res.json();
          throw new Error(res.statusText);
        }),
      ])
        .then(jsonArr => {
          dispatch(
            setCurrentChannel({ ...jsonArr[0], followers: jsonArr[1].data })
          );
        })
        .catch(err => reject(err));
    });
}

export function getChannelMessages({
  companyId,
  channelId,
  from = 0,
  size = 10,
}) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/${companyId}/channel/${channelId}/messages?from=${from}&size=${size}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export function getPublicChannelProfile({ channelId }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/channel/${channelId}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export function createChannel({
  companyId,
  name,
  logo: imageData,
  alternativeName: alternate_name,
  website,
  desc: description,
  tags,
  isPublic: is_public,
  types,
}) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;

      let maybeUploadImage;
      if (imageData) {
        maybeUploadImage = uploadImage({
          imageData,
          token,
          type: CHANNEL_IMAGE,
        });
      } else {
        maybeUploadImage = new Promise(resolve => resolve(null));
      }

      maybeUploadImage.then(imageKey => {
        const body = {
          name,
          alternate_name,
          website,
          description,
          tags,
          is_public,
          types,
        };
        if (imageKey) {
          body.display_logo = s3KeyToUrl(imageKey);
        }
        fetchAPI({
          version: '1.2',
          endpoint: `/companies/${companyId}/channel`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        })
          .then(res => handleReponse(res))
          .then(json => resolve(json));
      });
    });
}

export function editChannel({
  companyId,
  channelId,
  name,
  logo,
  alternativeName: alternate_name,
  website,
  desc: description,
  tags,
  isPublic: is_public,
  types,
}) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      const action = ({ url }) =>
        fetchAPI({
          version: '1.2',
          endpoint: `/companies/${companyId}/channel/${channelId}`,
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name,
            display_logo: url,
            alternate_name,
            website,
            description,
            tags,
            is_public,
            types,
          }),
        })
          .then(res => handleReponse(res))
          .then(json => {
            dispatch(
              setCurrentChannel({
                ...json.data,
                followers: getState().context.channel.followers,
              })
            );
            return resolve(json);
          });

      if (typeof logo === 'string') {
        action({ url: logo });
      } else {
        uploadImage({ imageData: logo, token, type: CHANNEL_IMAGE })
          .then(imageKey => action({ url: s3KeyToUrl(imageKey) }))
          .catch(e => reject(e));
      }
    });
}

export function deleteChannel(companyId, channelId) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetch(
        `${__API_HOST__}/api/v1.1/companies/${companyId}/channel/${channelId}`,
        {
          method: 'delete',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      )
        .then(res => res.json())
        .then(json => {
          if (json.error_code) {
            reject(json);
          }
          resolve(json.data);
        });
    });
}

export const SET_CURRENT_CHANNEL_FOLLOWERS = 'SET_CURRENT_CHANNEL_FOLLOWERS';
export const setCurrentChannelFollowers = followers => ({
  type: SET_CURRENT_CHANNEL_FOLLOWERS,
  followers,
});
export function getChannelFollowers({
  companyId,
  channelId,
  setAsCurrent = false,
}) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/${companyId}/channel/${channelId}/followers`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => {
          if (setAsCurrent) dispatch(setCurrentChannelFollowers(json.data));
          return resolve(json.data);
        });
    });
}

export function inviteToFollow({
  companyId,
  channelId,
  contacts,
  subject,
  message,
}) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/${companyId}/channel/${channelId}/invitation`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ contacts, subject, message }),
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export function removeFollower({ channelId, followerId }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/channel/${channelId}/followers/${followerId}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export function getInvitedEmails(companyId, channelId) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/${companyId}/channel/${channelId}/invitation`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => res.json())
        .then(json => {
          if (json.error_code) {
            reject(json);
          }
          resolve(json.data);
        });
    });
}

export function followChannel({ channelId: channel_id }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: '/me/companies/channel',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ channel_id }),
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export function unfollowChannel({ channelId }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/me/companies/channel/${channelId}`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export const UPDATE_MESSAGE_MAPS = 'UPDATE_MESSAGE_MAPS';
export const updateMessageMaps = ({
  channelId,
  followerMessageMap,
  followerAlertMap,
}) => ({
  type: UPDATE_MESSAGE_MAPS,
  channelId,
  followerMessageMap,
  followerAlertMap,
});

export const MARK_AS_READ = 'MARK_AS_READ';
export const markAsRead = ({ channelId, followerId }) => ({
  type: MARK_AS_READ,
  channelId,
  followerId,
});

export function getP2PMessagesSummary({ companyId, channelId }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint: `/companies/channel/${channelId}/messages/latest`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => {
          const channelAlertInfo = getState().ocMessage[channelId];
          if (channelAlertInfo) {
            const currentLatestMessageMap = channelAlertInfo.followerMessageMap;
            const currentAlertMap = channelAlertInfo.followerAlertMap;
            let change = false;
            const followerMessageMap = Object.assign(
              {},
              ...json.followers.map(r => {
                if (
                  r.message &&
                  r.message.id !== currentLatestMessageMap[r.follower.user_id]
                ) {
                  currentAlertMap[r.follower.user_id] = true;
                  change = true;
                }
                return { [r.follower.user_id]: r.message && r.message.id };
              })
            );
            if (change) {
              dispatch(
                updateMessageMaps({
                  channelId,
                  followerMessageMap,
                  followerAlertMap: currentAlertMap,
                })
              );
            }
          } else {
            const currentAlertMap = {};
            const followerMessageMap = Object.assign(
              {},
              ...json.followers.map(r => {
                currentAlertMap[r.follower.user_id] = true;
                return { [r.follower.user_id]: r.message && r.message.id };
              })
            );
            dispatch(
              updateMessageMaps({
                channelId,
                followerMessageMap,
                followerAlertMap: currentAlertMap,
              })
            );
          }
          return resolve(json);
        });
    });
}

export function getP2PMessagesWithFollower({
  companyId,
  channelId,
  followerId,
  from = 0,
  size = 10,
}) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const token = getState().users.token;
      fetchAPI({
        endpoint:
          `/companies/${companyId}/channel/${channelId}` +
          `/followers/${followerId}/messages?from=${from}&size=${size}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export function sendP2PMessagesToFollower({ followerId, type, message }) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const channelId = getState().context.channel.id;
      const token = getState().users.token;
      formatMessage({ type, message, token })
        .then(formattedMessage =>
          fetchAPI({
            endpoint: `/companies/channel/${channelId}/followers/${followerId}/messages`,
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formattedMessage),
          })
        )
        .then(res => handleReponse(res))
        .then(json => resolve(json));
    });
}

export const assignChannelAdmin = ({ channelId, employeeId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/companies/channel/${channelId}/employees/${employeeId}/manager`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleReponse(res))
      .then(json => {
        if (json.status === 1) {
          return resolve(json);
        }
        return reject(json);
      });
  });

export const removeChannelAdmin = ({ channelId, employeeId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/companies/channel/${channelId}/employees/${employeeId}/manager`,
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleReponse(res))
      .then(json => {
        if (json.status === 1) {
          return resolve(json);
        }
        return reject(json);
      });
  });

export const getChannelMessageByType = ({ type, channelId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/companies/channel/${channelId}/messages?MessageType=${type}`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleReponse(res))
      .then(json => {
        if (json.messages) return resolve(json.messages);
        return reject(json);
      });
  });

export const getChannelTypes = () => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: '/channels/types',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(json => {
        if (json.items) return resolve(json.items);
        return reject(json);
      });
  });



// WEBPACK FOOTER //
// ./app/actions/ChannelActionCreators.js